import { createReducer } from 'src/helpers/reducer';
import { RATE_LINE_SIDES } from '../../constants';
import type { RateLineSide } from '../../constants';
import type { IGridFilter, IRateLine, IRateLineHeader } from '../../sharedTypes';
import actionTypes from './actionTypes';
import type { ILoadLinesPayload, IRateLinesState } from './types';

const initState: IRateLinesState = {
  side: RATE_LINE_SIDES.PAY,
  headersByKey: {},
  dimensionsByKey: {},
  headersFailed: false,
  rows: [],
  page: 1,
  reachedEnd: false,
  filteredCount: 0,
  sortableColumns: [],
  filters: [],
  loading: false,
  loadFailed: false,
  failedPage: null,
  exporting: false,
};

function onLoadHeadersRequest(state: IRateLinesState): IRateLinesState {
  return { ...state, headersFailed: false };
}

function onLoadHeadersSuccess(
  state: IRateLinesState,
  { payload }: { payload: { cacheKey: string; headers: IRateLineHeader[]; dimensions: IRateLineHeader[] } },
): IRateLinesState {
  return {
    ...state,
    headersFailed: false,
    headersByKey: { ...state.headersByKey, [payload.cacheKey]: payload.headers },
    dimensionsByKey: { ...state.dimensionsByKey, [payload.cacheKey]: payload.dimensions },
  };
}

function onLoadHeadersError(state: IRateLinesState): IRateLinesState {
  return { ...state, headersFailed: true };
}

function onLoadLinesRequest(state: IRateLinesState, { payload }: { payload: ILoadLinesPayload }): IRateLinesState {
  const replacing = payload.query.page === 1;

  return {
    ...state,
    loading: true,
    loadFailed: false,
    failedPage: null,
    ...(replacing ? { rows: [], page: 1, reachedEnd: false } : {}),
  };
}

function onLoadLinesSuccess(
  state: IRateLinesState,
  {
    payload,
  }: {
    payload: {
      page: number;
      rows: IRateLine[];
      filteredCount: number;
      sortableColumns: string[];
      filters: IGridFilter[];
    };
  },
): IRateLinesState {
  const { rows, ...rest } = payload;

  return {
    ...state,
    ...rest,
    rows: payload.page > 1 ? [...state.rows, ...rows] : rows,
    // An empty later page ends the list, whatever `filteredCount` still claims.
    reachedEnd: payload.page > 1 && rows.length === 0,
    loading: false,
    loadFailed: false,
    failedPage: null,
  };
}

function onLoadLinesError(state: IRateLinesState, { payload }: { payload: { page: number } }): IRateLinesState {
  return { ...state, loading: false, loadFailed: true, failedPage: payload.page };
}

function onSideChanged(state: IRateLinesState, { payload }: { payload: { side: RateLineSide } }): IRateLinesState {
  // The two sides have disjoint column sets, so the outgoing rows cannot be shown under the
  // incoming side's header. The headers cache survives — it is keyed per side already.
  return {
    ...state,
    side: payload.side,
    rows: [],
    page: 1,
    reachedEnd: false,
    filters: [],
    loadFailed: false,
    failedPage: null,
  };
}

export default createReducer(initState, {
  [actionTypes.LOAD_HEADERS_REQUEST]: onLoadHeadersRequest,
  [actionTypes.LOAD_HEADERS_SUCCESS]: onLoadHeadersSuccess,
  [actionTypes.LOAD_HEADERS_ERROR]: onLoadHeadersError,
  [actionTypes.LOAD_LINES_REQUEST]: onLoadLinesRequest,
  [actionTypes.LOAD_LINES_SUCCESS]: onLoadLinesSuccess,
  [actionTypes.LOAD_LINES_ERROR]: onLoadLinesError,
  [actionTypes.EXPORT_REQUEST]: (state: IRateLinesState) => ({ ...state, exporting: true }),
  [actionTypes.EXPORT_SETTLED]: (state: IRateLinesState) => ({ ...state, exporting: false }),
  [actionTypes.SIDE_CHANGED]: onSideChanged,
  // An export outlives the section that started it, so a remount keeps the button held until it settles — on
  // whichever contract is up by then: the slice is one for all of them.
  [actionTypes.CLEAR_STORE]: (state: IRateLinesState) => ({ ...initState, exporting: state.exporting }),
});
